// libraries
import React, { useEffect } from 'react'
import { clone } from 'lodash'
import PropTypes from 'prop-types'
import { Typography, Box, Stack, IconButton, Tooltip } from '@mui/material_modern'
import DeleteIcon from '@mui/icons-material/Delete'
import { useSelector } from 'react-redux'

// specific components
import Input from 'components/generic/form/Input'
import Select from 'components/generic/form/Select'
import DatePicker from 'components/generic/form/DatePicker'
import InputFormat from 'components/generic/inputs/formatting/InputFormat'
import PaymentWarning from 'components/containers/bills/components/PaymentWarning'

// helpers and utils
import { setDecimalPrecision } from 'utils/helpers'
import { addToDate, capAllWords } from 'utils/data-manipulation/common'
import { ENTITY_IN_LOCK_PERIOD_MESSAGE, PAYMENT_METHODS_VALUES } from 'utils/constants'

const PaymentMethodRow = ({
  name,
  defaultMethod,
  availableMethods,
  totalNetValue,
  control,
  watch,
  setValue,
  paymentConfirmed,
  paymentMethods,
  setPaymentMethods,
  onClickDelete,
  recalculateTotalPaymentAmount,
  showPaymentDate,
  pricePrepend,
  paymentMethod,
  isMultiplePayments,
  listOfMaterialsOnText,
  multiplePaymentsTotalAmount,
  onUpdatePaymentMethod,
  groupedPayments,
  setGroupedPayments,
  paymentPageIsLocked,
}) => {
  const rowId = name.replace('payment.', '')
  const methodText = watch(`${name}.method`, 'Cash')

  const getPaymentMethodDisplay = method => {
    if (!method) return ''
    const upperMethod = method.toUpperCase()
    const entry = PAYMENT_METHODS_VALUES[upperMethod]
    return entry || method
  }

  const methodTextMapped = getPaymentMethodDisplay(methodText)
  const { id, isRestricted, materialTypeCommonName, restrictedThroughDate, totalAmount, gsPayStatus } = paymentMethod
  const { enableMaterialPaymentHolds } = useSelector(state => state.featureFlags?.variables || {})

  const { settings } = useSelector(state => state.settings)
  const { lockAccountingPeriod } = settings?.overviewSettings?.overview || {}

  // This will set total amount to the only payment method available
  useEffect(() => {
    if (paymentMethods.length === 1 && !paymentMethods[0].isRestricted) {
      setValue(
        `${name}.amount`,
        setDecimalPrecision(totalNetValue, {
          shouldFormatNumber: false,
          decimalPrecisionOverride: 2,
        }),
      )
    }
  }, [totalNetValue, paymentMethods, name])

  const getGroupedPaymentMethodsUpdated = (pMethodsGrouped, pMethods, newMethodType) => {
    const clonedGroupedPayments = clone(pMethodsGrouped)
    const { single, grouped } = clonedGroupedPayments
    let rowIdFound = false
    const singleArray = single.map(item => {
      if (item.id === rowId) {
        item.method = newMethodType
        rowIdFound = true
      }
      return item
    })

    let newPaymentMethods = []
    const groupedArray = grouped.map(group => {
      if (!rowIdFound && group.some(item => item.id === rowId)) {
        newPaymentMethods = pMethods.map(item =>
          group.find(gItem => gItem.id === item.id) ? { ...item, method: newMethodType } : item,
        )
        return group.map(item => ({ ...item, method: newMethodType }))
      }
      return group
    })
    const pMethodsGroupedCopy = { single: singleArray, grouped: groupedArray }
    setGroupedPayments(pMethodsGroupedCopy)
    return newPaymentMethods
  }

  return (
    <Tooltip title={paymentPageIsLocked ? ENTITY_IN_LOCK_PERIOD_MESSAGE : ''} placement="right">
      {enableMaterialPaymentHolds && (
        <PaymentWarning
          id={id}
          isRestricted={isRestricted}
          materialName={materialTypeCommonName}
          restrictedThroughDate={restrictedThroughDate}
          isMultiple={isMultiplePayments}
          multipleTooltipText={listOfMaterialsOnText}
        />
      )}
      <Stack
        direction="row"
        mb={2}
        pt={enableMaterialPaymentHolds && isRestricted ? 0 : 1}
        spacing={2}
        alignItems="center"
      >
        <Box flex={enableMaterialPaymentHolds && isRestricted && !paymentConfirmed ? 2 : 1}>
          {paymentConfirmed ? (
            <Stack direction="column" spacing={0}>
              <Typography color="textSecondary" variant="caption">
                Pay Method
              </Typography>
              <Typography>{methodTextMapped}</Typography>
            </Stack>
          ) : (
            <Select
              name={`${name}.method`}
              label="Pay Method"
              data-test={`${name}-method`}
              size="small"
              control={control}
              defaultValue={defaultMethod}
              items={availableMethods}
              InputLabelProps={{ shrink: true }}
              selectProps={{
                onChange: event => {
                  setValue(`${name}.method`, event.target.value)
                  let updatedPaymentMethods = paymentMethods.map(paymentMethod => ({
                    ...paymentMethod,
                    method: rowId === paymentMethod.id ? event.target.value : paymentMethod.method,
                  }))
                  if (isMultiplePayments) {
                    updatedPaymentMethods = getGroupedPaymentMethodsUpdated(
                      groupedPayments,
                      paymentMethods,
                      event.target.value,
                    )
                  }
                  setPaymentMethods(updatedPaymentMethods)
                  onUpdatePaymentMethod(updatedPaymentMethods)
                },
              }}
              disabled={paymentPageIsLocked}
            />
          )}
        </Box>
        <Box flex={1}>
          {enableMaterialPaymentHolds && isRestricted ? (
            <>
              {paymentConfirmed && (
                <Typography color="textSecondary" variant="caption">
                  Amount
                </Typography>
              )}
              <Typography
                textAlign={paymentConfirmed ? 'start' : 'end'}
                pr={7}
              >{`${pricePrepend}${setDecimalPrecision(isMultiplePayments ? multiplePaymentsTotalAmount : totalAmount, { decimalPrecisionOverride: 2 })}`}</Typography>
            </>
          ) : (
            <Input
              name={`${name}.amount`}
              label="Amount"
              data-test={`${name}-amount`}
              size="small"
              variant={paymentConfirmed ? 'standard' : 'outlined'}
              defaultValue="0"
              fractionDigits={2}
              control={control}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                readOnly: paymentConfirmed,
                ...(paymentConfirmed && { disableUnderline: true }),
                inputComponent: InputFormat,
                inputProps: {
                  name: 'amountPaid',
                  inputType: 'price',
                  pricePrepend,
                  onChange: recalculateTotalPaymentAmount(rowId),
                  decimalScale: 2,
                  step: 0.01,
                  min: 0,
                  style: {
                    textAlign: !enableMaterialPaymentHolds
                      ? 'left'
                      : enableMaterialPaymentHolds && paymentConfirmed
                        ? 'left'
                        : 'right',
                  },
                },
              }}
              sx={{ minWidth: '140px', maxWidth: '350px' }}
              disabled={paymentPageIsLocked || methodText === 'gs_pay'}
            />
          )}
        </Box>
        {showPaymentDate && (
          <Box flex={1}>
            <DatePicker
              name={`${name}.paymentDate`}
              label="Payment Date"
              data-test={`${name}-paymentDate`}
              variant="outlined"
              size="small"
              control={control}
              defaultDate={new Date()}
              InputLabelProps={{ shrink: true }}
              datePickerProps={{
                minDate: lockAccountingPeriod.isLockedForAccounting
                  ? addToDate(new Date(lockAccountingPeriod.lockAccountingDate))
                  : undefined,
              }}
              disabled={paymentPageIsLocked}
            />
          </Box>
        )}
        {methodText === 'gs_pay' && gsPayStatus && (
          <Stack direction="column" spacing={0}>
            <Typography color="textSecondary" variant="caption">
              Payment Status
            </Typography>
            <Typography>{gsPayStatus ? capAllWords(gsPayStatus.toLowerCase()) : '--'}</Typography>
          </Stack>
        )}
        {!paymentConfirmed && enableMaterialPaymentHolds && !isRestricted && (
          <IconButton
            color="default"
            size="small"
            disabled={paymentPageIsLocked || paymentMethods.length === 1}
            onClick={onClickDelete}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
    </Tooltip>
  )
}

PaymentMethodRow.propTypes = {
  name: PropTypes.string.isRequired,
  defaultMethod: PropTypes.string.isRequired,
  availableMethods: PropTypes.array,
  totalNetValue: PropTypes.number.isRequired,
  control: PropTypes.instanceOf(Object).isRequired,
  watch: PropTypes.func,
  setValue: PropTypes.func,
  paymentConfirmed: PropTypes.bool,
  paymentMethods: PropTypes.array,
  setPaymentMethods: PropTypes.func,
  onClickDelete: PropTypes.func,
  recalculateTotalPaymentAmount: PropTypes.func,
  showPaymentDate: PropTypes.bool,
  pricePrepend: PropTypes.string,
  paymentMethod: PropTypes.shape({
    id: PropTypes.string,
    isRestricted: PropTypes.bool,
    materialTypeCommonName: PropTypes.string,
    restrictedThroughDate: PropTypes.string,
    totalAmount: PropTypes.number,
    gsPayStatus: PropTypes.string,
  }),
  paymentStatus: PropTypes.string,
  isMultiplePayments: PropTypes.bool,
  listOfMaterialsOnText: PropTypes.string,
  multiplePaymentsTotalAmount: PropTypes.number,
  onUpdatePaymentMethod: PropTypes.func,
  groupedPayments: PropTypes.object,
  setGroupedPayments: PropTypes.func,
  paymentPageIsLocked: PropTypes.bool,
}

PaymentMethodRow.defaultProps = {
  availableMethods: [],
  watch: null,
  setValue: () => {},
  paymentConfirmed: false,
  paymentMethods: [],
  setPaymentMethods: () => {},
  onClickDelete: null,
  recalculateTotalPaymentAmount: () => {},
  showPaymentDate: false,
  pricePrepend: '$',
  paymentMethod: {},
  paymentStatus: '',
  isMultiplePayments: false,
  listOfMaterialsOnText: '',
  multiplePaymentsTotalAmount: 0,
  onUpdatePaymentMethod: () => {},
  groupedPayments: {},
  setGroupedPayments: () => {},
  paymentPageIsLocked: false,
}

export default PaymentMethodRow
